import {getStartOfDay, systemDateFormat} from '@lib/utils';
import {endOfDay} from 'date-fns';
import isSameDay from 'date-fns/fp/isSameDay';
import pipe from 'lodash/fp/pipe';
import {GroupWorkspaceReservation, ReservationMember, WorkspaceReservation} from '../entities';
import format from 'date-fns/format';
import {WorkspaceReservationsSliceState} from './types';
import parseISO from 'date-fns/parseISO';

export const selectWorkspaceReservationDate = (workspaceReservation: WorkspaceReservation) =>
  workspaceReservation.startDate;

export const workspaceReservationOnDay = (date: Date) =>
  pipe(selectWorkspaceReservationDate, getStartOfDay, isSameDay(date));

/**
 * Returns the selected space node (area, floor or building), as desks are not spaces and therefore cannot have more people working on it.
 * @param workspaceReservation The workspaceReservation.
 * @returns The selected space node id.
 */
export const getWorkspaceReservationSpaceNodeId = ({
  buildingId,
  floorId,
  areaId,
  deskId,
  nodeId,
}: {
  buildingId?: string;
  floorId?: string;
  areaId?: string;
  deskId?: string;
  nodeId: string;
}) => {
  if (nodeId !== deskId) {
    return nodeId;
  }
  if (areaId) {
    return areaId;
  }
  if (floorId) {
    return floorId;
  }
  if (buildingId) {
    return buildingId;
  }
  return '';
};

export const getWorkspaceReservationSpaceNodeName = (x: WorkspaceReservation): string => {
  if (x.areaName) {
    return `${x.floorName}, ${x.areaName}`;
  }
  if (x.floorName) {
    return x.floorName;
  }
  if (x.buildingName) {
    return x.buildingName;
  }

  return '';
};

export const updateCountForNodeWhenWorkspaceReservationIsRemoved = (
  state: WorkspaceReservationsSliceState,
  workspaceReservation: WorkspaceReservation,
  userId: string,
) => {
  const userReservation = workspaceReservation.members.find((member) => member.userId === userId);
  if (userReservation) {
    const isUserOwnerOfWorkspaceReservation = userReservation.isOwner;
    const date = format(parseISO(workspaceReservation.startDate), systemDateFormat);
    const occupationCount = isUserOwnerOfWorkspaceReservation
      ? getCountForMembersReservationOfWorkspaceReservation(workspaceReservation)
      : 1;
    const selectedSpaceNodeId = getWorkspaceReservationSpaceNodeId({
      nodeId: userReservation.nodeId,
      ...userReservation.location,
    });
    const nodesOnDate = state.workspaceReservationCountPerNodeOnDate[date];
    if (nodesOnDate) {
      const currentCountForSelectedNode = nodesOnDate[selectedSpaceNodeId] ?? 0;
      nodesOnDate[selectedSpaceNodeId] =
        currentCountForSelectedNode > 0 ? currentCountForSelectedNode - occupationCount : 0;
    }
  }
};

export const updateCountForNodeWhenGroupReservationIsAdded = (
  state: WorkspaceReservationsSliceState,
  groupWorkspaceReservation: GroupWorkspaceReservation,
) => {
  const date = format(parseISO(groupWorkspaceReservation.startDate), systemDateFormat);
  const count = groupWorkspaceReservation.invitees.length + 1;
  const selectedSpaceNodeId = getWorkspaceReservationSpaceNodeId({
    nodeId: groupWorkspaceReservation.owner.nodeId,
    ...groupWorkspaceReservation.owner.location,
  });
  const nodesOnDate = state.workspaceReservationCountPerNodeOnDate[date];
  if (nodesOnDate) {
    const currentCountForSelectedNode = nodesOnDate[selectedSpaceNodeId] ?? 0;
    nodesOnDate[selectedSpaceNodeId] = currentCountForSelectedNode + count;
  }
};

export const getWorkspaceReservationDateRange = (workspaceReservation: WorkspaceReservation) => {
  const from = getStartOfDay(workspaceReservation.startDate);
  const to = endOfDay(from);
  return {from: from.toISOString(), to: to.toISOString()};
};

/**
 * @param workspaceReservation
 * @returns count for pending and confirmed reservation of members in workspace reservation
 */
export const getCountForMembersReservationOfWorkspaceReservation = (
  workspaceReservation?: WorkspaceReservation,
): number => {
  if (!workspaceReservation) {
    return 0;
  }
  return (
    workspaceReservation.members.filter(
      (member) => member.status === 'Confirmed' || member.status === 'InvitationPending',
    ).length ?? 1
  );
};

export const updateOccupationCountForNodeWhenWorkspaceIsUpdated = (
  oldWorkspaceReservation: WorkspaceReservation | undefined,
  updatedWorkspaceReservation: WorkspaceReservation,
  state: WorkspaceReservationsSliceState,
) => {
  const oldWorkspaceReservationCount = getCountForMembersReservationOfWorkspaceReservation(oldWorkspaceReservation);
  const updatedWorkspaceReservationCount =
    getCountForMembersReservationOfWorkspaceReservation(updatedWorkspaceReservation);
  const oldSelectedSpaceNodeId = oldWorkspaceReservation
    ? getWorkspaceReservationSpaceNodeId(oldWorkspaceReservation)
    : '';
  const newSelectedSpaceNodeId = getWorkspaceReservationSpaceNodeId(updatedWorkspaceReservation);

  const date = format(parseISO(updatedWorkspaceReservation.startDate), systemDateFormat);

  const nodesOnDate = state.workspaceReservationCountPerNodeOnDate[date];
  if (nodesOnDate) {
    if (oldSelectedSpaceNodeId) {
      const countForOldSelectedNode = nodesOnDate[oldSelectedSpaceNodeId] ?? 0;
      nodesOnDate[oldSelectedSpaceNodeId] =
        countForOldSelectedNode > 0 ? countForOldSelectedNode - oldWorkspaceReservationCount : 0;
    }
    nodesOnDate[newSelectedSpaceNodeId] = (nodesOnDate[newSelectedSpaceNodeId] ?? 0) + updatedWorkspaceReservationCount;
  }
};

export const getWorkspaceReservationByGroupReservationId = (
  allWorkspaceReservation: WorkspaceReservation[],
  groupReservationId: string,
): WorkspaceReservation | undefined =>
  allWorkspaceReservation.find(
    (workspaceReservation) => workspaceReservation.groupReservationId === groupReservationId,
  );

export const extractedUserDisplayName = ({firstName, lastName, email}: ReservationMember['user']) =>
  firstName && lastName ? `${firstName} ${lastName}` : email;
